import React from 'react';
import { CrossSceneSuggestion, crossSceneLabel } from './crossScene';

interface CrossSceneBlockProps {
  suggestion: CrossSceneSuggestion;
  /**
   * Re-runs the target scene with the corrective note. Omitted where nothing
   * can address another scene, and the block is then the suggestion alone.
   */
  onFix?: (target: { sceneId: string; what: string }) => Promise<void>;
  /** The host is already busy with a regeneration of its own. */
  disabled?: boolean;
}

/**
 * What a verdict says about an EARLIER scene: the fix the reviewer proposes,
 * why, and — when the engine deliberately left it to the operator — the button
 * that carries it out.
 *
 * The button is offered exactly when the engine says it refused (permission
 * off, cap spent, loop guard) and never when it already acted: an applied fix
 * is reported, not offered a second time.
 */
const CrossSceneBlock: React.FC<CrossSceneBlockProps> = ({ suggestion, onFix, disabled }) => {
  const [confirming, setConfirming] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [requested, setRequested] = React.useState(false);
  const [error, setError] = React.useState('');

  const label = crossSceneLabel(suggestion.sceneId);
  // The button does exactly one thing, so it is offered for exactly that one.
  // An absent action is today's engine saying nothing where there is only one
  // thing to say; a DIFFERENT one is a request this button cannot carry out.
  const isRegenerate = !suggestion.action || suggestion.action.toLowerCase() === 'regenerate';
  // The fix has to be carriable AND wanted: with nothing to say to the target
  // scene there is no regeneration to ask for, only a re-roll of the same plan.
  const canFix =
    Boolean(onFix) &&
    suggestion.suggested &&
    !suggestion.applied &&
    isRegenerate &&
    Boolean(suggestion.what);

  const apply = async () => {
    if (!onFix) return;
    setApplying(true);
    setError('');
    try {
      await onFix({ sceneId: suggestion.sceneId, what: suggestion.what });
      setConfirming(false);
      // The identical button, back where it was, is a second generation two
      // clicks away for a fix that is already running. Say what happened
      // instead; the verdict's own stamp takes over once the run records it.
      setRequested(true);
    } catch (err) {
      // The confirm stays open on a refusal, so the retry is one click away.
      setError(err instanceof Error && err.message ? err.message : 'Could not fix that scene.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      data-testid="review-verdict-cross-scene"
      className="space-y-1 border border-white/10 bg-black/40 px-2 py-1.5"
    >
      <p className="text-[10px] uppercase tracking-wide text-zinc-500">Earlier scene</p>
      <p className="text-[11px] leading-relaxed text-zinc-300 whitespace-pre-wrap">
        {suggestion.what ? `Scene ${label}: ${suggestion.what}` : `Scene ${label}`}
      </p>
      {suggestion.why && (
        <p className="text-[10px] leading-relaxed text-zinc-500 whitespace-pre-wrap">
          {suggestion.why}
        </p>
      )}

      {suggestion.applied && (
        <p data-testid="review-verdict-cross-scene-applied" className="text-[10px] text-zinc-400">
          Reviewer regenerated scene {crossSceneLabel(suggestion.applied.sceneId) || label}
        </p>
      )}

      {suggestion.suggested && !suggestion.applied && suggestion.suggestedReason && (
        <p className="text-[10px] italic text-zinc-500">
          Not applied: {suggestion.suggestedReason}
        </p>
      )}

      {canFix && requested && (
        <p data-testid="review-verdict-cross-scene-requested" className="text-[10px] text-sky-300">
          Requested — scene {label} is being re-run.
        </p>
      )}

      {canFix && !confirming && !requested && (
        <button
          type="button"
          data-testid="review-verdict-cross-scene-fix"
          className="btn btn-sm btn-secondary text-[10px]"
          disabled={disabled}
          onClick={() => {
            setError('');
            setConfirming(true);
          }}
        >
          Fix scene {label}
        </button>
      )}

      {canFix && confirming && (
        <div
          data-testid="review-verdict-cross-scene-confirm"
          className="space-y-1 border border-amber-400/40 bg-amber-500/10 px-2 py-1.5"
        >
          {/* Stated before the button, as every other regenerate here is:
              nothing downstream moves, because this one does not cascade. */}
          <p className="text-[10px] text-amber-200">
            Re-runs scene {label} only, with the reviewer&apos;s note. Later scenes are left alone.
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              data-testid="review-verdict-cross-scene-apply"
              className="btn btn-sm btn-primary text-[10px]"
              disabled={applying || disabled}
              onClick={() => void apply()}
            >
              {applying ? 'Applying…' : 'Confirm'}
            </button>
            <button
              type="button"
              data-testid="review-verdict-cross-scene-cancel"
              className="btn btn-sm btn-ghost text-[10px]"
              disabled={applying}
              onClick={() => {
                // Backing out drops the refusal with it: it was about the
                // attempt the operator just abandoned.
                setError('');
                setConfirming(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <p data-testid="review-verdict-cross-scene-error" className="text-[10px] text-red-300">
          {error}
        </p>
      )}
    </div>
  );
};

export default CrossSceneBlock;
